import { Texture, TextureLoader, Vector3 } from "three";
import { useLoader } from "@react-three/fiber";
import React, { useEffect, useState } from "react";
import { OptionTemplate } from "../../../shared/api/middleware-api-client";
import { useConfigurationOptionsContext } from "../../../app/providers/DoorOptionsProvider";
import { getFullAssetPath } from "../../../shared/utils/assets";
import { newTextureXLXLDoors } from "../../../features/option-controls/door-width-controls/XLXXLHelper";

interface BackgroundProps {
  door: OptionTemplate | null;
}

const Background: React.FC<BackgroundProps> = ({ door }) => {
  const { layout, eigenwijs, isXLXXLScene } = useConfigurationOptionsContext();

  const [meshScale, setMeshScale] = useState<Vector3>(new Vector3(0, 0, 0));
  const [meshPosition, setMeshPosition] = useState<Vector3>(
    new Vector3(0, 0, 0),
  );

  const interiorOpen1Unit2UnitTexture = useLoader(
    TextureLoader,
    getFullAssetPath("/img-v2/interior-open-1-unit-2-unit-v2.jpg"),
  );
  const interiorOpen3Unit4UnitTexture = useLoader(
    TextureLoader,
    getFullAssetPath("/img-v2/interior-open-3-unit-4-unit.jpg"),
  );
  const interiorOpenStairsTexture = useLoader(
    TextureLoader,
    getFullAssetPath("/img-v2/interior-open-stairs.jpg"),
  );
  const interiorStudioTexture = useLoader(
    TextureLoader,
    getFullAssetPath("/img-v2/interior-studio.jpg"),
  );

  const interiorXLXXLTexture = useLoader(
    TextureLoader,
    getFullAssetPath("/img-v2/interior-xlxxl-open.jpg"),
  );

  const backgroundRecord: Record<string, Texture> = {
    opdekdeurmetkozijn: interiorOpen1Unit2UnitTexture,
    opdekdeur: interiorOpen1Unit2UnitTexture,
    stompedeur: interiorOpen1Unit2UnitTexture,
    // schuifdeur: interiorOpen1Unit2UnitTexture,
    schuifdeur: interiorOpen3Unit4UnitTexture,
    taatsdeur: interiorOpen1Unit2UnitTexture,
    paneel: interiorOpen1Unit2UnitTexture,
    scharnierdeur: interiorOpen1Unit2UnitTexture,
    paneelpaneel: interiorOpen1Unit2UnitTexture,
    paneelpaneelpaneel: interiorOpen3Unit4UnitTexture,
    paneelpaneelpaneelpaneel: interiorOpen3Unit4UnitTexture,
    dubbel: interiorOpen1Unit2UnitTexture,
    dubbel1paneel: interiorOpen3Unit4UnitTexture,
    dubbel2paneel: interiorOpen3Unit4UnitTexture,
    taatsdeurtaatsdeurpaneelpaneel: interiorOpen3Unit4UnitTexture,
    taatsdeurpaneelpaneel: interiorOpen3Unit4UnitTexture,
    taatsdeur2paneel: interiorOpen3Unit4UnitTexture,
    taatsdeur1paneel: interiorOpen1Unit2UnitTexture,
    schuifdeurpaneelpaneel: interiorOpen3Unit4UnitTexture,
    schuifdeurpaneelpaneelschuifdeur: interiorOpen3Unit4UnitTexture,
    schuifdeurschuifdeurpaneelpaneel: interiorOpen3Unit4UnitTexture,
    schuifdeurpaneelschuifdeur: interiorOpen3Unit4UnitTexture,
    schuifpaneelschuiftrap: interiorOpenStairsTexture,
    dubbeleschuifdeur1paneel: interiorOpen3Unit4UnitTexture,
    dubbeleschuifdeur2paneel: interiorOpen3Unit4UnitTexture,
    dubbeleschuifdeur: interiorOpen3Unit4UnitTexture,
    schuifdeur2paneel: interiorOpen3Unit4UnitTexture,
    schuifdeur1paneel: interiorOpen1Unit2UnitTexture,
    scharnierdeur1paneel: interiorOpen1Unit2UnitTexture,
    scharnierdeur2paneel: interiorOpen3Unit4UnitTexture,
    dubbelescharnierdeur: interiorOpen1Unit2UnitTexture,
    dubbelescharnierdeur1paneel: interiorOpen3Unit4UnitTexture,
    dubbelescharnierdeur2paneel: interiorOpen3Unit4UnitTexture,
    scharnierdeurpaneelscharnierdeur: interiorOpen3Unit4UnitTexture,
    scharnierpaneelscharniertrap: interiorOpenStairsTexture,
    scharnierdeurpaneelpaneel: interiorOpen3Unit4UnitTexture,
    scharnierdeurpaneelpaneelscharnierdeur: interiorOpen3Unit4UnitTexture,
    scharnierdeurscharnierdeurpaneelpaneel: interiorOpen3Unit4UnitTexture,
  };

  useEffect(() => {
    if (eigenwijs) {
      if (door && door.configuration.nElements) {
        updateMeshScale(door.configuration.nElements);
        updateMeshPosition(door.configuration.nElements);
      }
    }
  }, [eigenwijs?.sizeObjects]);

  const updateMeshScale = (elements: number) => {
    if (eigenwijs) {
      if (
        layout &&
        layout.code === "eigenwijs" &&
        door &&
        door.code.includes("trap")
      ) {
        setMeshScale(new Vector3(5.5, 5.5, 1));
        return;
      }

      if (elements <= 2) {
        setMeshScale(new Vector3(5.25, 5.39, 1));
      } else {
        setMeshScale(new Vector3(5.5, 4.9, 1));
      }
    }
    if (door && newTextureXLXLDoors.includes(door.code) && isXLXXLScene) {
      setMeshScale(new Vector3(5.5, 4.4, 1));
    }
  };

  const updateMeshPosition = (elements: number) => {
    if (eigenwijs) {
      if (
        layout &&
        layout.code === "eigenwijs" &&
        door &&
        door.code.includes("trap")
      ) {
        setMeshPosition(new Vector3(-0.05, 0.2, -0.7));
        return;
      }

      if (elements <= 2) {
        setMeshPosition(new Vector3(0, 0.45, -0.7));
      } else {
        setMeshPosition(new Vector3(0, 0.25, -0.3));
      }
    }
  };

  const getBackgroundRecord = (code: string) => {
    if (newTextureXLXLDoors.includes(code) && isXLXXLScene) {
      return interiorXLXXLTexture;
    }
    if (layout && layout.code === "eigenwijs" && code.includes("trap")) {
      return interiorStudioTexture;
    } else {
      return backgroundRecord[code];
    }
  };

  return (
    <>
      {door && (
        <mesh position={meshPosition} scale={meshScale}>
          <planeGeometry />
          <meshStandardMaterial map={getBackgroundRecord(door.code)} />
        </mesh>
      )}
    </>
  );
};
export default Background;
